import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';
import FormLayout from '../../../components/Common/FormLayout';
import VehicleChecklistPersonalizado from '../View/VehicleChecklistPersonalizado';
import VehicleAgendamentos from '../View/VehicleAgendamentos';

interface VehicleFormData {
  placa: string;
  tag?: string;
  marca: string;
  modelo: string;
  tipo: string;
  ano_fabricacao: string;
  cor: string;
  chassi: string;
  renavam: string;
  quilometragem: number | '';
  capacidade_tanque: number | '';
  capacidade: number | '';
  tipo_combustivel: string;
  status: 'ativo' | 'inativo' | 'manutencao';
  observacoes: string;
  ultima_inspecao: string;
  proxima_inspecao: string;
  combustivel: string;
  categoria: string;
  valor_fipe: number | '';
  valor_seguro: number | '';
  numero_motor: string;
  potencia: string;
  cilindradas: string;
  peso_bruto: number | '';
  capacidade_carga: number | '';
  numero_passageiros: number | '';
  proprietario: string;
  documento_veiculo: string;
  certificado_registro: string;
  data_aquisicao: string;
  data_venda: string;
  localizacao_atual: string;
  centro_custo: string;
  departamento_responsavel: string;
  foto_veiculo: string;
}

const VehicleEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<VehicleFormData>({
    placa: '',
    tag: '',
    marca: '',
    modelo: '',
    tipo: 'Caminhão Leve (3/4)',
    ano_fabricacao: '',
    cor: '',
    chassi: '',
    renavam: '',
    quilometragem: '',
    capacidade_tanque: '',
    capacidade: '',
    tipo_combustivel: 'diesel',
    status: 'ativo',
    observacoes: '',
    ultima_inspecao: '',
    proxima_inspecao: '',
    combustivel: '',
    categoria: '',
    valor_fipe: '',
    valor_seguro: '',
    numero_motor: '',
    potencia: '',
    cilindradas: '',
    peso_bruto: '',
    capacidade_carga: '',
    numero_passageiros: '',
    proprietario: '',
    documento_veiculo: '',
    certificado_registro: '',
    data_aquisicao: '',
    data_venda: '',
    localizacao_atual: '',
    centro_custo: '',
    departamento_responsavel: '',
    foto_veiculo: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [activeTab, setActiveTab] = useState<'detalhes' | 'checklist' | 'agendamentos'>('detalhes');

  useEffect(() => {
    if (id) {
      loadVehicleData();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVehicleData = async () => {
    try {
      setIsLoadingData(true);
      const response = await apiService.getVehicle(Number(id)) as ApiResponse<any>;
      
      if (response.success && response.data) {
        const vehicle = response.data;
        setFormData({
      placa: vehicle.placa || '',
      tag: vehicle.tag || '',
      marca: vehicle.marca || '',
      modelo: vehicle.modelo || '',
          tipo: vehicle.tipo || 'Caminhão',
          ano_fabricacao: vehicle.ano_fabricacao ? String(vehicle.ano_fabricacao) : '',
          cor: vehicle.cor || '',
          chassi: vehicle.chassi || '',
          renavam: vehicle.renavam || '',
          quilometragem: vehicle.quilometragem_atual || vehicle.quilometragem || '',
          capacidade_tanque: vehicle.capacidade_tanque || '',
          capacidade: vehicle.capacidade || '',
          tipo_combustivel: vehicle.tipo_combustivel || vehicle.combustivel || 'diesel',
          status: vehicle.status || 'ativo',
          observacoes: vehicle.observacoes || '',
          ultima_inspecao: vehicle.ultima_inspecao || '',
          proxima_inspecao: vehicle.proxima_inspecao || '',
          combustivel: vehicle.combustivel || '',
          categoria: vehicle.categoria || '',
          valor_fipe: vehicle.valor_fipe || '',
          valor_seguro: vehicle.valor_seguro || '',
          numero_motor: vehicle.numero_motor || '',
          potencia: vehicle.potencia || '',
          cilindradas: vehicle.cilindradas || '',
          peso_bruto: vehicle.peso_bruto || '',
          capacidade_carga: vehicle.capacidade_carga || '',
          numero_passageiros: vehicle.numero_passageiros || '',
          proprietario: vehicle.proprietario || '',
          documento_veiculo: vehicle.documento_veiculo || '',
          certificado_registro: vehicle.certificado_registro || '',
          data_aquisicao: vehicle.data_aquisicao || '',
          data_venda: vehicle.data_venda || '',
          localizacao_atual: vehicle.localizacao_atual || '',
          centro_custo: vehicle.centro_custo || '',
          departamento_responsavel: vehicle.departamento_responsavel || '',
          foto_veiculo: vehicle.foto_veiculo || ''
        });
      } else {
        throw new Error(response.message || 'Erro ao carregar dados do veículo');
      }
    } catch (error: any) {
      console.error('Erro ao carregar veículo:', error);
      utils.showNotification(error.message || 'Erro ao carregar dados do veículo', { type: 'error' });
      navigate('/veiculos');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name as keyof VehicleFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.placa.trim()) {
      newErrors.placa = 'Placa é obrigatória';
    } else if (!/^[A-Z]{3}\d{4}$|^[A-Z]{3}\d{1}[A-Z]{1}\d{2}$/i.test(formData.placa)) {
      newErrors.placa = 'Formato de placa inválido (ex: ABC1234 ou ABC1A23)';
    }

    if (!formData.marca.trim()) {
      newErrors.marca = 'Marca é obrigatória';
    }

    if (!formData.modelo.trim()) {
      newErrors.modelo = 'Modelo é obrigatório';
    }

    if (!formData.tipo.trim()) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    if (!formData.ano_fabricacao.trim()) {
      newErrors.ano_fabricacao = 'Ano de fabricação/modelo é obrigatório';
    } else {
      // Validar formato YYYY ou YYYY/YYYY
      const anoPattern = /^(\d{4})(\/\d{4})?$/;
      if (!anoPattern.test(formData.ano_fabricacao)) {
        newErrors.ano_fabricacao = 'Formato inválido. Use YYYY ou YYYY/YYYY (ex: 2024 ou 2024/2025)';
      } else {
        // Validar anos se estiverem no formato correto
        const anos = formData.ano_fabricacao.split('/');
        const anoAtual = new Date().getFullYear();
        const anoFabricacao = parseInt(anos[0]);

        if (anoFabricacao < 1900 || anoFabricacao > anoAtual + 1) {
          newErrors.ano_fabricacao = 'Ano de fabricação deve estar entre 1900 e ' + (anoAtual + 1);
        }

        if (anos.length === 2) {
          const anoModelo = parseInt(anos[1]);
          if (anoModelo < 1900 || anoModelo > anoAtual + 2) {
            newErrors.ano_fabricacao = 'Ano do modelo deve estar entre 1900 e ' + (anoAtual + 2);
          }
          if (anoModelo < anoFabricacao) {
            newErrors.ano_fabricacao = 'Ano do modelo não pode ser anterior ao ano de fabricação';
          }
        }
      }
    }

    if (formData.quilometragem === '') {
      newErrors.quilometragem = 'Quilometragem é obrigatória';
    } else if (Number(formData.quilometragem) < 0) {
      newErrors.quilometragem = 'Quilometragem não pode ser negativa';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      // Função para mapear tipo de veículo para categoria do backend
      const mapearCategoria = (tipo: string): string => {
        const tipoLower = (tipo || '').toLowerCase();

        // Na edição o "tipo" já vem reduzido à categoria (ex.: 'caminhao'); nesse
        // caso retornamos o próprio valor para não corromper o registro ao salvar.
        const categoriasValidas = ['caminhao', 'van', 'carro', 'motocicleta', 'onibus', 'maquina'];
        if (categoriasValidas.includes(tipoLower)) {
          return tipoLower;
        }

        // Caminhões e derivados
        if (tipoLower.includes('caminhão') || tipoLower.includes('carreta') ||
            tipoLower.includes('bitrem') || tipoLower.includes('tritrem') ||
            tipoLower.includes('rodotrem') || tipoLower.includes('truck')) {
          return 'caminhao';
        }

        // Vans e utilitários
        if (tipoLower.includes('van') || tipoLower.includes('furgão') ||
            tipoLower.includes('fiorino') || tipoLower.includes('kombi') ||
            tipoLower.includes('sprinter') || tipoLower.includes('ducato') ||
            tipoLower.includes('hr') || tipoLower.includes('utilitário')) {
          return 'van';
        }

        // Carros
        if (tipoLower.includes('carro') || tipoLower.includes('hatchback') ||
            tipoLower.includes('sedan') || tipoLower.includes('suv') ||
            tipoLower.includes('pickup') || tipoLower.includes('crossover')) {
          return 'carro';
        }

        // Motos e similares
        if (tipoLower.includes('moto') || tipoLower.includes('triciclo') ||
            tipoLower.includes('quadriciclo') || tipoLower.includes('ciclomotor')) {
          return 'motocicleta';
        }

        // Ônibus
        if (tipoLower.includes('ônibus') || tipoLower.includes('onibus') ||
            tipoLower.includes('micro-ônibus')) {
          return 'onibus';
        }

        // Máquinas, equipamentos e outros
        return 'maquina';
      };

      const categoria = mapearCategoria(formData.tipo);

      // Preparar dados para envio - mapear campos para o formato esperado pelo backend
      const submitData = {
        placa: formData.placa,
        tag: formData.tag || undefined,
        marca: formData.marca,
        modelo: formData.modelo,
        tipo: categoria, // usar 'tipo' em vez de 'categoria' para o campo NOT NULL
        categoria: categoria, // manter categoria também se necessário
        ano_fabricacao: formData.ano_fabricacao || undefined,
        cor: formData.cor,
        chassi: formData.chassi,
        renavam: formData.renavam,
        quilometragem_atual: formData.quilometragem === '' ? undefined : Number(formData.quilometragem), // backend espera 'quilometragem_atual'
        capacidade_tanque: formData.capacidade_tanque === '' ? undefined : Number(formData.capacidade_tanque),
        capacidade: formData.capacidade === '' ? undefined : Number(formData.capacidade),
        combustivel: formData.tipo_combustivel, // backend espera 'combustivel' em vez de 'tipo_combustivel'
        status: formData.status,
        observacoes: formData.observacoes,
        ultima_inspecao: formData.ultima_inspecao || undefined,
        proxima_inspecao: formData.proxima_inspecao || undefined,
        valor_fipe: formData.valor_fipe === '' ? undefined : Number(formData.valor_fipe),
        valor_seguro: formData.valor_seguro === '' ? undefined : Number(formData.valor_seguro),
        numero_motor: formData.numero_motor || undefined,
        potencia: formData.potencia || undefined,
        cilindradas: formData.cilindradas || undefined,
        peso_bruto: formData.peso_bruto === '' ? undefined : Number(formData.peso_bruto),
        capacidade_carga: formData.capacidade_carga === '' ? undefined : Number(formData.capacidade_carga),
        numero_passageiros: formData.numero_passageiros === '' ? undefined : Number(formData.numero_passageiros),
        proprietario: formData.proprietario || undefined,
        documento_veiculo: formData.documento_veiculo || undefined,
        certificado_registro: formData.certificado_registro || undefined,
        data_aquisicao: formData.data_aquisicao || undefined,
        data_venda: formData.data_venda || undefined,
        localizacao_atual: formData.localizacao_atual || undefined,
        centro_custo: formData.centro_custo || undefined,
        departamento_responsavel: formData.departamento_responsavel || undefined,
        foto_veiculo: formData.foto_veiculo || undefined
      };

      console.log('Dados enviados para atualização:', submitData);
      const response = await apiService.updateVehicle(Number(id), submitData);
      
      if (response.success) {
        utils.showNotification('Veículo atualizado com sucesso!', { type: 'success' });
        navigate('/veiculos');
      } else {
        throw new Error(response.message || 'Erro ao atualizar veículo');
      }
    } catch (error: any) {
      console.error('Erro ao atualizar veículo:', error);
      utils.showNotification(error.message || 'Erro ao atualizar veículo', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    // Criar um evento simulado para chamar handleSubmit
    const fakeEvent = {
      preventDefault: () => {}
    } as React.FormEvent;
    handleSubmit(fakeEvent);
  };

  const handleCancel = () => {
    navigate('/veiculos');
  };

  if (isLoadingData) {
    return (
      <FormLayout
        title="Carregando Veículo..."
        isLoading={true}
        onCancel={handleCancel}
        showActions={false}
      >
        <div></div>
      </FormLayout>
    );
  }

  return (
    <FormLayout
      title="Editar Veículo"
      subtitle="Edição de Veículo"
      entityId={id}
      onCancel={handleCancel}
      onSave={handleSave}
      saveButtonText="Atualizar Veículo"
      isLoading={isLoading}
      showActions={false}
    >
      {/* Navegação por Abas */}
      <div className="card">
        <div className="card-header">
          <div className="tab-navigation">
            <button 
              className={`tab-button ${activeTab === 'detalhes' ? 'active' : ''}`}
              onClick={() => setActiveTab('detalhes')}
            >
              <i className="fas fa-info-circle"></i>
              Detalhes do Veículo
            </button>
            <button 
              className={`tab-button ${activeTab === 'checklist' ? 'active' : ''}`}
              onClick={() => setActiveTab('checklist')}
            >
              <i className="fas fa-clipboard-check"></i>
              Checklist Personalizado
            </button>
            <button 
              className={`tab-button ${activeTab === 'agendamentos' ? 'active' : ''}`}
              onClick={() => setActiveTab('agendamentos')}
            >
              <i className="fas fa-clock"></i>
              Agendamentos
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo da Aba Detalhes */}
      {activeTab === 'detalhes' && (
        <form onSubmit={handleSubmit}>
              {/* Informações Básicas */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Informações Básicas
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Placa *</label>
                    <input
                      type="text"
                      name="placa"
                      value={formData.placa}
                      onChange={handleInputChange}
                      className={`form-control ${errors.placa ? 'error' : ''}`}
                      placeholder="ABC1234"
                      maxLength={8}
                    />
                    {errors.placa && <span className="error-message">{errors.placa}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">TAG</label>
                    <input
                      type="text"
                      name="tag"
                      value={formData.tag || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: TAG001"
                      maxLength={50}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Marca *</label>
                    <input
                      type="text"
                      name="marca"
                      value={formData.marca}
                      onChange={handleInputChange}
                      className={`form-control ${errors.marca ? 'error' : ''}`}
                      placeholder="Ex: Mercedes-Benz"
                    />
                    {errors.marca && <span className="error-message">{errors.marca}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Modelo *</label>
                    <input
                      type="text"
                      name="modelo"
                      value={formData.modelo}
                      onChange={handleInputChange}
                      className={`form-control ${errors.modelo ? 'error' : ''}`}
                      placeholder="Ex: Atego"
                    />
                    {errors.modelo && <span className="error-message">{errors.modelo}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Tipo *</label>
                    <select
                      name="tipo"
                      value={formData.tipo}
                      onChange={handleInputChange}
                      className={`form-control ${errors.tipo ? 'error' : ''}`}
                    >
                      <optgroup label="Caminhões">
                        <option value="Caminhão Leve (3/4)">Caminhão Leve (3/4)</option>
                        <option value="Caminhão Toco">Caminhão Toco</option>
                        <option value="Caminhão Truck">Caminhão Truck</option>
                        <option value="Carreta (Semirreboque)">Carreta (Semirreboque)</option>
                        <option value="Bitrem">Bitrem</option>
                        <option value="Tritrem">Tritrem</option>
                        <option value="Rodotrem">Rodotrem</option>
                        <option value="Caminhão Basculante">Caminhão Basculante</option>
                        <option value="Caminhão Baú">Caminhão Baú</option>
                        <option value="Caminhão Munck">Caminhão Munck</option>
                      </optgroup>
                      <optgroup label="Utilitários e Comerciais">
                        <option value="Van">Van</option>
                        <option value="Furgão">Furgão</option>
                        <option value="HR">HR</option>
                        <option value="Fiorino">Fiorino</option>
                        <option value="Kombi">Kombi</option>
                        <option value="Sprinter">Sprinter</option>
                        <option value="Ducato">Ducato</option>
                        <option value="Utilitário Pequeno">Utilitário Pequeno</option>
                      </optgroup>
                      <optgroup label="Carros de Passeio">
                        <option value="Carro de Passeio">Carro de Passeio</option>
                        <option value="Hatchback">Hatchback</option>
                        <option value="Sedan">Sedan</option>
                        <option value="SUV">SUV</option>
                        <option value="Pickup">Pickup</option>
                        <option value="Crossover">Crossover</option>
                      </optgroup>
                      <optgroup label="Motos e Similares">
                        <option value="Moto">Moto</option>
                        <option value="Motocicleta">Motocicleta</option>
                        <option value="Motoneta">Motoneta</option>
                        <option value="Triciclo">Triciclo</option>
                        <option value="Quadriciclo">Quadriciclo</option>
                        <option value="Ciclomotor">Ciclomotor</option>
                      </optgroup>
                      <optgroup label="Transporte de Passageiros">
                        <option value="Ônibus">Ônibus</option>
                        <option value="Micro-ônibus">Micro-ônibus</option>
                        <option value="Van Escolar">Van Escolar</option>
                        <option value="Van de Turismo">Van de Turismo</option>
                      </optgroup>
                      <optgroup label="Máquinas e Equipamentos">
                        <option value="Empilhadeira">Empilhadeira</option>
                        <option value="Trator">Trator</option>
                        <option value="Retroescavadeira">Retroescavadeira</option>
                        <option value="Escavadeira">Escavadeira</option>
                        <option value="Pá Carregadeira">Pá Carregadeira</option>
                        <option value="Máquina Agrícola">Máquina Agrícola</option>
                        <option value="Máquina de Construção">Máquina de Construção</option>
                      </optgroup>
                      <optgroup label="Outros">
                        <option value="Reboque">Reboque</option>
                        <option value="Semireboque">Semireboque</option>
                        <option value="Trailer">Trailer</option>
                        <option value="Outro">Outro</option>
                      </optgroup>
                    </select>
                    {errors.tipo && <span className="error-message">{errors.tipo}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Ano de Fabricação/Modelo *</label>
                    <input
                      type="text"
                      name="ano_fabricacao"
                      value={formData.ano_fabricacao}
                      onChange={handleInputChange}
                      className={`form-control ${errors.ano_fabricacao ? 'error' : ''}`}
                      placeholder="2024/2025"
                      maxLength={9}
                    />
                    {errors.ano_fabricacao && <span className="error-message">{errors.ano_fabricacao}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Cor</label>
                    <input
                      type="text"
                      name="cor"
                      value={formData.cor}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: Branco"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Capacidade</label>
                    <input
                      type="number"
                      name="capacidade"
                      value={formData.capacidade}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: 1000"
                      min="0"
                      step="1"
                    />
                  </div>
                </div>
              </div>

              {/* Documentos e Identificação */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Documentos e Identificação
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Número do Chassi</label>
                    <input
                      type="text"
                      name="chassi"
                      value={formData.chassi}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: 9BWZZZ377VT004251"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">RENAVAM</label>
                    <input
                      type="text"
                      name="renavam"
                      value={formData.renavam}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: 12345678901"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Número do Motor</label>
                    <input
                      type="text"
                      name="numero_motor"
                      value={formData.numero_motor}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: 1234567890ABCDE"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Proprietário</label>
                    <input
                      type="text"
                      name="proprietario"
                      value={formData.proprietario}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Nome do proprietário"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Documento do Veículo</label>
                    <input
                      type="text"
                      name="documento_veiculo"
                      value={formData.documento_veiculo}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Número do documento"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Certificado de Registro</label>
                    <input
                      type="text"
                      name="certificado_registro"
                      value={formData.certificado_registro}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Número do certificado"
                    />
                  </div>
                </div>
              </div>

              {/* Especificações Técnicas */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Especificações Técnicas
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Quilometragem Atual *</label>
                    <input
                      type="number"
                      name="quilometragem"
                      value={formData.quilometragem}
                      onChange={handleInputChange}
                      className={`form-control ${errors.quilometragem ? 'error' : ''}`}
                      placeholder="0"
                      min="0"
                      step="1"
                    />
                    {errors.quilometragem && <span className="error-message">{errors.quilometragem}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Capacidade do Tanque (L)</label>
                    <input
                      type="number"
                      name="capacidade_tanque"
                      value={formData.capacidade_tanque}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: 200"
                      min="0"
                      step="1"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Tipo de Combustível</label>
                    <select
                      name="tipo_combustivel"
                      value={formData.tipo_combustivel}
                      onChange={handleInputChange}
                      className="form-control"
                    >
                      <option value="diesel">Diesel</option>
                      <option value="gasolina">Gasolina</option>
                      <option value="etanol">Etanol</option>
                      <option value="flex">Flex</option>
                      <option value="gnv">GNV</option>
                      <option value="eletrico">Elétrico</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="form-control"
                    >
                      <option value="ativo">Ativo</option>
                      <option value="inativo">Inativo</option>
                      <option value="manutencao">Em Manutenção</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Potência</label>
                    <input
                      type="text"
                      name="potencia"
                      value={formData.potencia}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: 150 cv"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Cilindradas</label>
                    <input
                      type="text"
                      name="cilindradas"
                      value={formData.cilindradas}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: 2000 cc"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Peso Bruto (kg)</label>
                    <input
                      type="number"
                      name="peso_bruto"
                      value={formData.peso_bruto}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: 3500"
                      min="0"
                      step="1"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Capacidade de Carga (kg)</label>
                    <input
                      type="number"
                      name="capacidade_carga"
                      value={formData.capacidade_carga}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: 1500"
                      min="0"
                      step="1"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Número de Passageiros</label>
                    <input
                      type="number"
                      name="numero_passageiros"
                      value={formData.numero_passageiros}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: 5"
                      min="0"
                      step="1"
                    />
                  </div>
                </div>
              </div>

              {/* Valores */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Valores
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Valor FIPE (R$)</label>
                    <input
                      type="number"
                      name="valor_fipe"
                      value={formData.valor_fipe}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: 85000"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Valor do Seguro (R$)</label>
                    <input
                      type="number"
                      name="valor_seguro"
                      value={formData.valor_seguro}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: 3500"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              {/* Datas */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Datas Importantes
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Data de Aquisição</label>
                    <input
                      type="date"
                      name="data_aquisicao"
                      value={formData.data_aquisicao}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Data de Venda</label>
                    <input
                      type="date"
                      name="data_venda"
                      value={formData.data_venda}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Última Inspeção</label>
                    <input
                      type="date"
                      name="ultima_inspecao"
                      value={formData.ultima_inspecao}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Próxima Inspeção</label>
                    <input
                      type="date"
                      name="proxima_inspecao"
                      value={formData.proxima_inspecao}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>

              {/* Localização e Contabilidade */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Localização e Contabilidade
                </h3>
                
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                  <div className="form-group">
                    <label className="form-label">Localização Atual</label>
                    <input
                      type="text"
                      name="localizacao_atual"
                      value={formData.localizacao_atual}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: Pátio Central"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Centro de Custo</label>
                    <input
                      type="text"
                      name="centro_custo"
                      value={formData.centro_custo}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: CC-001"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Departamento Responsável</label>
                    <input
                      type="text"
                      name="departamento_responsavel"
                      value={formData.departamento_responsavel}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: Logística"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Foto do Veículo (URL)</label>
                    <input
                      type="text"
                      name="foto_veiculo"
                      value={formData.foto_veiculo}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Ex: https://exemplo.com/foto.jpg"
                    />
                  </div>
                </div>
              </div>

              {/* Observações */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  Observações
                </h3>
                
                <div className="form-group">
                  <label className="form-label">Observações</label>
                  <textarea
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Informações adicionais sobre o veículo..."
                    rows={4}
                  />
                </div>
              </div>

              {/* Ações do Formulário */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="loading"></span>
                      Atualizando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Atualizar Veículo
                    </>
                  )}
                </button>
              </div>
            </form>
      )}

      {/* Conteúdo da Aba Checklist Personalizado */}
      {activeTab === 'checklist' && (
        <VehicleChecklistPersonalizado />
      )}

      {/* Conteúdo da Aba Agendamentos */}
      {activeTab === 'agendamentos' && (
        <VehicleAgendamentos />
      )}
    </FormLayout>
  );
};

export default VehicleEdit;
